import LegalLayout, { Section } from "@/pages/LegalLayout";
import { BRAND } from "@/data/site";

const Ember = ({ children }) => <span className="text-ember">{children}</span>;

export default function Privacy() {
  return (
    <LegalLayout eyebrow="Legal" title="Privacy Policy" updated="June 1, 2026">
      <p className="font-body text-lg text-zinc-300 leading-relaxed">
        ReBarOS ("we", "us", "our") is committed to protecting your privacy. This
        Privacy Policy explains what information we collect, how we use and store
        it, and the rights you have over your data when you use the ReBarOS mobile
        application and related services (the "Service").
      </p>

      <Section heading="1. Account Data">
        <p>
          When you create a ReBarOS account we collect information you provide
          directly, such as your name, email address, and authentication
          credentials. We use this data to create and secure your account,
          authenticate you, provide customer support, and communicate important
          account and service updates.
        </p>
      </Section>

      <Section heading="2. Health & Fitness Data">
        <p>
          To deliver personalized coaching, ReBarOS collects and stores
          health- and fitness-related information you choose to provide,
          including body metrics, training logs, lifted loads, workout history,
          nutrition and macro entries, hydration logs, injury flags, and
          progress photos.
        </p>
        <p>
          This data is stored securely in a <Ember>MongoDB</Ember> database and is
          used solely to power your training programs, nutrition guidance, and
          progress tracking. We do not sell your health or fitness data.
        </p>
      </Section>

      <Section heading="3. Third-Party Services">
        <p>We rely on a limited set of trusted third-party services to operate the Service:</p>
        <ul className="list-none space-y-3 pt-2">
          <li className="flex gap-3">
            <span className="text-ember font-bold">·</span>
            <span><Ember>Stripe</Ember> — processes subscription payments and billing. Payment card details are handled directly by Stripe and are never stored on our servers.</span>
          </li>
          <li className="flex gap-3">
            <span className="text-ember font-bold">·</span>
            <span><Ember>OpenAI</Ember> and <Ember>Google Gemini</Ember> — power our AI features such as personalized programs and meal ideas. Relevant, minimized inputs are sent to generate responses.</span>
          </li>
          <li className="flex gap-3">
            <span className="text-ember font-bold">·</span>
            <span><Ember>OpenFoodFacts</Ember> — provides nutrition and barcode data for our food and macro tracking features.</span>
          </li>
        </ul>
        <p>
          Each third-party service processes data under its own privacy policy.
          We share only the minimum information necessary for each service to
          function.
        </p>
      </Section>

      <Section heading="4. How We Use Your Information">
        <p>
          We use your information to provide, maintain, and improve the Service;
          personalize your training and nutrition experience; process payments;
          respond to support requests; ensure security; and comply with legal
          obligations. We do not use your health and fitness data for advertising.
        </p>
      </Section>

      <Section heading="5. Data Retention & Security">
        <p>
          We retain your data for as long as your account is active or as needed
          to provide the Service. We apply industry-standard technical and
          organizational measures to protect your data, including encryption in
          transit and access controls. No method of transmission or storage is
          100% secure, but we work continuously to safeguard your information.
        </p>
      </Section>

      <Section heading="6. Your Rights">
        <p>
          Depending on your jurisdiction, you may have the right to access,
          correct, export, or delete your personal data, and to object to or
          restrict certain processing. You can manage most of your data directly
          within the app.
        </p>
      </Section>

      <Section heading="7. Data Deletion & Contact">
        <p>
          To request deletion of your account and associated data, or for any
          privacy-related question, contact us at{" "}
          <a href={`mailto:${BRAND.email}`} className="text-ember hover:underline" data-testid="privacy-contact-email">
            {BRAND.email}
          </a>
          . We will respond to verified requests within a reasonable timeframe in
          accordance with applicable law.
        </p>
      </Section>

      <Section heading="8. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes
          will be communicated through the app or via email. Continued use of the
          Service after changes take effect constitutes acceptance of the updated
          policy.
        </p>
      </Section>
    </LegalLayout>
  );
}
